import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, unref as _unref, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { columns: "2" }
const _hoisted_4 = ["value", "disabled"]
const _hoisted_5 = ["value", "disabled"]
const _hoisted_6 = ["value", "disabled"]
const _hoisted_7 = { key: 2 }
const _hoisted_8 = ["value"]
const _hoisted_9 = { key: 3 }
const _hoisted_10 = { columns: "2" }
const _hoisted_11 = ["value", "disabled"]
const _hoisted_12 = ["value", "disabled"]
const _hoisted_13 = { key: 4 }
const _hoisted_14 = { class: "archive-row" }
const _hoisted_15 = ["value", "label", "required"]
const _hoisted_16 = { slot: "footer" }
const _hoisted_17 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { validateSettings } from '../../utils/settings';
import Banner from '@components/Banner/Banner.vue';
import ChartSettings from '../settings/ChartSettings.vue';
import { ChartSetting } from '../../models/catalogservice/ui-types';
import {
  useCreateAppChart,
  useUpdateAppChart,
  usePushAppChart,
} from '../../queries/useAppChartsMutations';
import {
  AppChart,
  AppChartUpdateRequest,
  AppChartCreateRequest,
} from '../../models/appcharts/ui-types';

import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';

const MAX_ARCHIVE_BYTES = 32 * 1024 * 1024;


export default /*@__PURE__*/_defineComponent({
  __name: 'ChartsModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');

const initialValues = ref<AppChart | null>(null);
const viewCanEdit = ref(false);

// Form fields (separate from the model to avoid proxy mutation issues)
const chartName = ref('');
const chartShortDescription = ref('');
const chartDescription = ref('');
const helmChartUrl = ref('');
const helmRepoUrl = ref('');
const chartSettings = ref<ChartSetting[]>([]);

// Mirrors maxChartArchiveSize on the server, so an oversized file is named here
// instead of failing as a truncated upload.
const chartSourceTypes = [
  { label: 'Helm URL', value: 'url' },
  { label: 'Upload Archive', value: 'upload' },
];
const chartSource = ref<'url' | 'upload'>('url');
const chartArchive = ref<File | null>(null);
const archiveError = ref('');
const archiveFileInput = ref<HTMLInputElement | null>(null);

const isEdit = computed(() => modalMode.value === 'edit');
const isView = computed(() => modalMode.value === 'view');

// The upload source of a create. An edit keeps the url fields, and replaces
// the archive of a stored chart through canReplaceArchive.
const isUpload = computed(() => modalMode.value === 'create' && chartSource.value === 'upload');

// Repointing a chart that applications are bound to changes what they deploy on their
// next rebuild, with no version bump and no record. The server refuses it, so lock the
// location instead.
const isLockedByApps = computed(() => isEdit.value && !!initialValues.value?.boundApps);

// A chart uploaded to Epinio lives in a repository of its own, named after the chart:
// oci://<registry>/epinio-charts/<name>. The server never lets its location change, as
// the stored archive would be left behind. The response does not mark such charts, so
// this mirrors the path check of the server (mayBeStored). The server has the last word.
const isStoredByEpinio = computed(() => {
  const name = initialValues.value?.meta?.name;
  const repo = initialValues.value?.helmRepo || '';

  return !!name && repo.startsWith('oci://') && repo.endsWith(`/epinio-charts/${ name }`);
});

const isLocationLocked = computed(() => isEdit.value && (isLockedByApps.value || isStoredByEpinio.value));

// The chart of a stored chart is replaced by uploading a new archive under the same name.
// The server refuses that while applications use the chart, so it is not offered then.
const canReplaceArchive = computed(() => isEdit.value && isStoredByEpinio.value && !isLockedByApps.value);

const {mutateAsync: createAppChart, isPending: isCreatingAppChart, isError: createAppChartError, error: createAppChartErrorData} = useCreateAppChart(store, () => {
  handleSuccess('create');
  closeModal();
});
const {mutateAsync: updateAppChart, isPending: isUpdatingAppChart, isError: updateAppChartError, error: updateAppChartErrorData} = useUpdateAppChart(store, () => {
  handleSuccess('update');
  closeModal();
});
const {mutateAsync: pushAppChart, isPending: isPushingAppChart, isError: pushAppChartError, error: pushAppChartErrorData} = usePushAppChart(store, () => {
  handleSuccess('create');
  closeModal();
});

// Replacing the archive of an existing chart. The other changes of the form are saved first,
// without closing the modal, the push then finishes the edit.
const {mutateAsync: updateAppChartQuietly, isPending: isUpdatingBeforeReplace, isError: updateBeforeReplaceError, error: updateBeforeReplaceErrorData} = useUpdateAppChart(store);
const {mutateAsync: replaceAppChartArchive, isPending: isReplacingArchive, isError: replaceArchiveError, error: replaceArchiveErrorData} = usePushAppChart(store, () => {
  handleSuccess('update');
  closeModal();
});

const isSaving = computed(() => {
  return isCreatingAppChart.value ||
    isUpdatingAppChart.value ||
    isPushingAppChart.value ||
    isUpdatingBeforeReplace.value ||
    isReplacingArchive.value;
});

const saveError = computed(() => {
  if (createAppChartError.value) return createAppChartErrorData.value;
  if (updateAppChartError.value) return updateAppChartErrorData.value;
  if (pushAppChartError.value) return pushAppChartErrorData.value;
  if (updateBeforeReplaceError.value) return updateBeforeReplaceErrorData.value;
  if (replaceArchiveError.value) return replaceArchiveErrorData.value;

  return null;
});

const hasSaveError = computed(() => {
  return createAppChartError.value ||
    updateAppChartError.value ||
    pushAppChartError.value ||
    updateBeforeReplaceError.value ||
    replaceArchiveError.value;
});

const isDirty = computed(() => {
  return dirtyFields.value.name ||
    dirtyFields.value.shortDescription ||
    dirtyFields.value.description ||
    dirtyFields.value.helmChart ||
    dirtyFields.value.helmRepo ||
    dirtyFields.value.settings ||
    !!chartArchive.value;
});

const dirtyFields = computed(() => {
  const fields: Partial<
    Record<keyof AppChartCreateRequest, boolean>
  > = {};

  fields.name = chartName.value !== (initialValues.value?.meta.name || '');
  fields.shortDescription = chartShortDescription.value !== (initialValues.value?.shortDescription || '');
  fields.description = chartDescription.value !== (initialValues.value?.description || '');
  fields.helmChart = helmChartUrl.value !== (initialValues.value?.helmChart || '');
  fields.helmRepo = helmRepoUrl.value !== (initialValues.value?.helmRepo || '');
  fields.settings = !isEqual(sortBy(chartSettings.value, 'name'), sortBy(initialValues.value?.settings || [], 'name'));

  return fields;
});

const showDiscardConfirm = ref(false);

const validationPassed = computed(() => {
  if (!chartName.value) return false;
  if (!chartShortDescription.value) return false;
  if (!chartDescription.value) return false;

  if (isUpload.value) {
    if (!chartArchive.value || archiveError.value) return false;
  } else {
    if (!helmRepoUrl.value && !helmChartUrl.value) return false;

    const settingsValid = validateSettings(chartSettings.value);
    if (!settingsValid) return false;
  }

  const nameErrors = validateKubernetesName(chartName.value, '', store.getters, undefined, []);
  return nameErrors.length === 0;
});

const canSave = computed(() => {
  const dirty = isDirty.value;
  const valid = validationPassed.value;
  return dirty && valid && !isSaving.value;
});

function openCreate() {
  modalMode.value = 'create';
  chartName.value = '';
  chartShortDescription.value = '';
  chartDescription.value = '';
  helmChartUrl.value = '';
  helmRepoUrl.value = '';
  chartSettings.value = [];
  chartSource.value = 'url';
  chartArchive.value = null;
  archiveError.value = '';
  showModal.value = true;
}

function onChartSourceChange(value: 'url' | 'upload') {
  chartSource.value = value;
  if (value === 'upload') {
    helmChartUrl.value = '';
    helmRepoUrl.value = '';
    chartSettings.value = [];
  } else {
    chartArchive.value = null;
    archiveError.value = '';
  }
}

function handleArchiveFileClick() {
  archiveFileInput.value?.click();
}

function handleArchiveFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = ''; // so the same file can be picked again after a failed push

  if (!file) return;

  if (file.size > MAX_ARCHIVE_BYTES) {
    chartArchive.value = null;
    archiveError.value = 'The chart archive is larger than the 32 MiB the server accepts.';
    return;
  }

  chartArchive.value = file;
  archiveError.value = '';
}

function openEdit(row: AppChart) {
  modalMode.value = 'edit';
  initialValues.value = row;
  chartName.value = row.meta?.name || '';
  chartShortDescription.value = row.shortDescription || '';
  chartDescription.value = row.description || '';
  helmChartUrl.value = row.helmChart || '';
  helmRepoUrl.value = row.helmRepo || '';
  chartSettings.value = row.settings || [];
  chartSource.value = 'url';
  chartArchive.value = null;
  archiveError.value = '';
  showModal.value = true;
}

// Read-only look at a chart, opened from its name in the list. canEdit decides whether
// the view offers to switch to editing.
function openView(row: AppChart, canEdit = false) {
  openEdit(row);
  modalMode.value = 'view';
  viewCanEdit.value = canEdit;
}

function handleModalClose() {
  if (isDirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  // Clear form state before setting showModal = false so that when Lit fires
  // modal-close (which triggers handleModalClose), isDirty is already false
  chartName.value = '';
  chartShortDescription.value = '';
  chartDescription.value = '';
  helmChartUrl.value = '';
  helmRepoUrl.value = '';
  chartSettings.value = [];
  chartSource.value = 'url';
  chartArchive.value = null;
  archiveError.value = '';
  showDiscardConfirm.value = false;
  showModal.value = false;
  initialValues.value = null;
}

const buildCreateRequest = (): AppChartCreateRequest => {
  const request: AppChartCreateRequest = {
    name: chartName.value,
    shortDescription: chartShortDescription.value,
    description: chartDescription.value,
  };
  if (helmChartUrl.value) {
    request.helmChart = helmChartUrl.value;
  }
  if (helmRepoUrl.value) {
    request.helmRepo = helmRepoUrl.value;
  }
  if (chartSettings.value.length > 0) {
    request.settings = chartSettings.value;
  }
  return request;
};

const buildUpdateRequest = (): AppChartUpdateRequest => {
  const request: AppChartUpdateRequest = {};

  if (dirtyFields.value.name) {
    request.name = chartName.value;
  }

  if (dirtyFields.value.description) {
    request.description = chartDescription.value;
  }

  if (dirtyFields.value.shortDescription) {
    request.shortDescription = chartShortDescription.value;
  }

  if (dirtyFields.value.helmChart) {
    request.helmChart = helmChartUrl.value;
  }

  if (dirtyFields.value.helmRepo) {
    request.helmRepo = helmRepoUrl.value;
  }

  if (dirtyFields.value.settings) {
    request.settings = chartSettings.value;
  }

  return request;
};

async function onSubmit() {
  if (!validationPassed.value || !isDirty.value || isSaving.value)
    return;

  if (isUpload.value && chartArchive.value) {
    await pushAppChart({ request: {
      name: chartName.value,
      description: chartDescription.value,
      shortDescription: chartShortDescription.value,
      archive: chartArchive.value,
    } });
  } else if (canReplaceArchive.value && initialValues.value && chartArchive.value) {
    // Read the form before the first request. A successful save resets it.
    const name = initialValues.value.meta.name;
    const pushRequest = {
      name,
      description: chartDescription.value,
      shortDescription: chartShortDescription.value,
      archive: chartArchive.value,
    };

    // The push carries the descriptions. What is left for the update are the settings.
    const request = buildUpdateRequest();
    delete request.description;
    delete request.shortDescription;

    if (Object.keys(request).length > 0) {
      await updateAppChartQuietly({ name, request });
    }
    await replaceAppChartArchive({ request: pushRequest });
  } else if (isEdit.value && initialValues.value) {
    const request = buildUpdateRequest();
    await updateAppChart({ name: initialValues.value.meta.name, request });
  } else {
    const request = buildCreateRequest();
    await createAppChart({ request });
  }
}

const handleSuccess = (type: 'create' | 'update') => {
  store.dispatch('growl/success', {
    title:   t(`epinio.growl.appCharts.${type}.success.title`),
    message: t(`epinio.growl.appCharts.${type}.success.message`, { name: chartName.value }),
  });
};

__expose({ openCreate, openEdit, openView });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    ".dismissible": false,
    title: (isView.value || isEdit.value) ? chartName.value || 'App Chart' : 'App Chart',
    subtitle: (isView.value || isEdit.value) ? '' : 'Create New',
    position: "top",
    onModalClose: handleModalClose
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("trailhand-form-card", null, [
        (initialValues.value?.boundApps)
          ? (_openBlock(), _createBlock(Banner, {
              key: 0,
              color: "warning",
              label: isLockedByApps.value
            ? 'One or more applications use this chart, so its location is locked. Repointing it would change what those applications deploy on their next rebuild. Description and settings can still be edited.'
            : 'This chart is currently associated with one or more applications. Editing it may cause issues for future rebuilds.'
            }, null, 8, ["label"]))
          : (isEdit.value && isStoredByEpinio.value)
            ? (_openBlock(), _createBlock(Banner, {
                key: 1,
                color: "info",
                label: "This chart is stored in Epinio's registry, so its location is locked. To use a different chart, select a new archive below. Description and settings can still be edited."
              }))
            : _createCommentVNode("", true),
        _cache[16] || (_cache[16] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", _hoisted_3, [
          _createElementVNode("trailhand-text-input", {
            value: chartName.value,
            label: "Name",
            placeholder: "A Unique Name",
            required: true,
            disabled: isView.value || isEdit.value,
            onTextInputChange: _cache[0] || (_cache[0] = (e) => { chartName.value = e.detail.value; })
          }, null, 40, _hoisted_4),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _createElementVNode("trailhand-text-input", {
            value: chartShortDescription.value,
            label: "Short Description",
            placeholder: "A brief description",
            required: true,
            disabled: isView.value,
            onTextInputChange: _cache[1] || (_cache[1] = (e) => { chartShortDescription.value = e.detail.value; })
          }, null, 40, _hoisted_5)
        ]),
        _cache[17] || (_cache[17] = _createTextVNode()),
        _createElementVNode("trailhand-form-row", null, [
          _createElementVNode("trailhand-text-area", {
            value: chartDescription.value,
            label: "Description",
            placeholder: "A detailed description",
            disabled: isView.value,
            required: "",
            onTextAreaChange: _cache[2] || (_cache[2] = (e) => { chartDescription.value = e.detail.value; })
          }, null, 40, _hoisted_6)
        ]),
        _cache[18] || (_cache[18] = _createTextVNode()),
        (!isEdit.value && !isView.value)
          ? (_openBlock(), _createElementBlock("trailhand-form-row", _hoisted_7, [
              _createElementVNode("trailhand-dropdown", {
                options: chartSourceTypes,
                value: chartSource.value,
                label: "Chart Source",
                required: true,
                onDropdownChange: _cache[3] || (_cache[3] = (e) => onChartSourceChange(e.detail.value))
              }, null, 40, _hoisted_8)
            ]))
          : _createCommentVNode("", true),
        _cache[19] || (_cache[19] = _createTextVNode()),
        (!isUpload.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _cache[10] || (_cache[10] = _createElementVNode("label", { style: {"font-size":"11px","color":"var(--th-input-label)"} }, [
                _createTextVNode("Helm URLs - Provide at least one of the following: "),
                _createElementVNode("span", { style: {"color":"var(--th-color-red)"} }, "*")
              ], -1)),
              _cache[11] || (_cache[11] = _createTextVNode()),
              _createElementVNode("trailhand-form-row", _hoisted_10, [
                _createElementVNode("trailhand-text-input", {
                  value: helmChartUrl.value,
                  label: "Helm Chart URL",
                  placeholder: "e.g. https://example.com/charts/mychart-0.1.0.tgz",
                  disabled: isView.value || isLocationLocked.value,
                  onTextInputChange: _cache[4] || (_cache[4] = (e) => { helmChartUrl.value = e.detail.value; })
                }, null, 40, _hoisted_11),
                _cache[9] || (_cache[9] = _createTextVNode()),
                _createElementVNode("trailhand-text-input", {
                  value: helmRepoUrl.value,
                  label: "Helm Repo URL",
                  placeholder: "e.g. https://example.com/charts/index.yaml",
                  disabled: isView.value || isLocationLocked.value,
                  onTextInputChange: _cache[5] || (_cache[5] = (e) => { helmRepoUrl.value = e.detail.value; })
                }, null, 40, _hoisted_12)
              ])
            ]))
          : _createCommentVNode("", true),
        _cache[20] || (_cache[20] = _createTextVNode()),
        (isUpload.value || canReplaceArchive.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("trailhand-text-input", {
                  style: {"flex":"1"},
                  value: chartArchive.value?.name || '',
                  label: isUpload.value ? 'Chart Archive' : 'Replace Chart Archive',
                  placeholder: "A .tgz produced by 'helm package'",
                  disabled: true,
                  required: isUpload.value
                }, null, 8, _hoisted_15),
                _cache[12] || (_cache[12] = _createTextVNode()),
                _createElementVNode("trailhand-button", {
                  variant: "alternate",
                  onButtonClick: handleArchiveFileClick
                }, "\n              Select File\n            ", 32),
                _cache[13] || (_cache[13] = _createTextVNode()),
                _createElementVNode("input", {
                  ref_key: "archiveFileInput",
                  ref: archiveFileInput,
                  type: "file",
                  class: "hidden-file-input",
                  accept: ".tgz,.tar.gz",
                  onChange: handleArchiveFileChange
                }, null, 544)
              ]),
              _cache[14] || (_cache[14] = _createTextVNode()),
              (archiveError.value)
                ? (_openBlock(), _createBlock(Banner, {
                    key: 0,
                    color: "error",
                    label: archiveError.value
                  }, null, 8, ["label"]))
                : _createCommentVNode("", true),
              _cache[15] || (_cache[15] = _createTextVNode()),
              _createVNode(Banner, {
                color: "info",
                label: isUpload.value
              ? 'Epinio stores the archive in its own registry. Chart settings can be added afterwards by editing the chart.'
              : 'The new archive replaces the chart in Epinio\'s registry. Settings are kept. This is only possible while no application uses the chart.'
              }, null, 8, ["label"])
            ]))
          : _createCommentVNode("", true),
        _cache[21] || (_cache[21] = _createTextVNode()),
        (!isUpload.value)
          ? (_openBlock(), _createBlock(ChartSettings, {
              key: 5,
              modelValue: chartSettings.value,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((chartSettings).value = $event)),
              disabled: isView.value,
              "allow-defaults": ""
            }, null, 8, ["modelValue", "disabled"]))
          : _createCommentVNode("", true)
      ]),
      _cache[22] || (_cache[22] = _createTextVNode()),
      (hasSaveError.value)
        ? (_openBlock(), _createBlock(Banner, {
            key: 0,
            color: "error",
            label: saveError.value?.message || _unref(t)('epinio.appCharts.errors.save')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true)
    ]),
    _cache[28] || (_cache[28] = _createTextVNode()),
    _createElementVNode("div", _hoisted_16, [
      (isView.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: closeModal
            }, "\n          Close\n        ", 32),
            _cache[23] || (_cache[23] = _createTextVNode()),
            (viewCanEdit.value)
              ? (_openBlock(), _createElementBlock("trailhand-button", {
                  key: 0,
                  variant: "primary",
                  onButtonClick: _cache[7] || (_cache[7] = ($event: any) => (modalMode.value = 'edit'))
                }, "\n          Edit Configuration\n        ", 32))
              : _createCommentVNode("", true)
          ], 64))
        : (showDiscardConfirm.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _cache[24] || (_cache[24] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
              _cache[25] || (_cache[25] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleKeepEditing
              }, "\n          Keep Editing\n        ", 32),
              _cache[26] || (_cache[26] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "destructive",
                onButtonClick: handleDiscard
              }, "\n          Discard\n        ", 32)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createElementVNode("trailhand-button", {
                variant: "secondary",
                class: "mr-10",
                onButtonClick: handleModalClose
              }, "\n          Cancel\n        ", 32),
              _cache[27] || (_cache[27] = _createTextVNode()),
              _createElementVNode("trailhand-button", {
                variant: "primary",
                disabled: !canSave.value,
                onButtonClick: onSubmit
              }, _toDisplayString(isEdit.value ? (isSaving.value ? _unref(t)('generic.updating') : _unref(t)('generic.save')) : (isSaving.value ? _unref(t)('generic.creating') : _unref(t)('generic.create'))), 41, _hoisted_17)
            ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})